/**
 * Severity colour palette — central source of truth.
 *
 * These values match the CSS `--severity-*` tokens in App.css. Use the
 * helpers below from any component that needs to render a severity-coloured
 * surface. Don't reintroduce inline hex literals.
 */

export type Severity =
  | 'EMERGENCY'
  | 'WATCH_AND_ACT'
  | 'ADVICE'
  | 'NOT_APPLICABLE'
  | 'OUT_OF_AREA'
  | string
  | undefined;

const PALETTE = {
  emergency: '#dc2626',
  watch: '#f97316',
  advice: '#fbbf24',
  baseline: '#9ca3af',
} as const;

/** Returns the foreground colour for a severity (markers, borders, badges). */
export function severityColor(severity: Severity): string {
  switch (severity) {
    case 'EMERGENCY':
      return PALETTE.emergency;
    case 'WATCH_AND_ACT':
      return PALETTE.watch;
    case 'ADVICE':
      return PALETTE.advice;
    default:
      return PALETTE.baseline;
  }
}

/** Returns a translucent background for severity-tinted panels/banners. */
export function severityBg(severity: Severity, alpha = 0.12): string {
  const hex = severityColor(severity);
  // Convert "#rrggbb" -> "rgba(r, g, b, alpha)"
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Human-readable label for a severity (e.g. for aria-labels). */
export function severityLabel(severity: Severity): string {
  switch (severity) {
    case 'EMERGENCY':
      return 'Emergency';
    case 'WATCH_AND_ACT':
      return 'Watch and Act';
    case 'ADVICE':
      return 'Advice';
    default:
      return 'Other';
  }
}

/** Numeric rank, lower = more urgent. Useful for sorting fire lists. */
export function severityRank(severity: Severity): number {
  switch (severity) {
    case 'EMERGENCY':
      return 0;
    case 'WATCH_AND_ACT':
      return 1;
    case 'ADVICE':
      return 2;
    case 'NOT_APPLICABLE':
      return 3;
    default:
      return 4;
  }
}

// ============================================================================
// Containment status — RFS "OUT_OF_CONTROL / BEING_CONTROLLED / UNDER_CONTROL /
// PATROL". Lives next to severity because the two are paired on every fire
// detail surface.
// ============================================================================

export type ContainmentStatus =
  | 'OUT_OF_CONTROL'
  | 'BEING_CONTROLLED'
  | 'UNDER_CONTROL'
  | 'PATROL'
  | string
  | undefined;

const STATUS_PALETTE = {
  outOfControl: '#dc2626',      // = --severity-emergency
  beingControlled: '#f97316',   // = --severity-watch
  underControl: '#10b981',      // green — distinct from severity palette
  patrol: '#3b82f6',            // blue — distinct from severity palette
  baseline: '#6b7280',          // gray
} as const;

/**
 * Returns the colour for a fire's containment status. Three call sites
 * previously kept their own copy of this map (Map.tsx, FireDetailPanel.tsx,
 * MapSettings3D.tsx) and could drift out of sync. Now there is one.
 */
export function statusColor(status: ContainmentStatus): string {
  switch (status) {
    case 'OUT_OF_CONTROL':
      return STATUS_PALETTE.outOfControl;
    case 'BEING_CONTROLLED':
      return STATUS_PALETTE.beingControlled;
    case 'UNDER_CONTROL':
      return STATUS_PALETTE.underControl;
    case 'PATROL':
      return STATUS_PALETTE.patrol;
    default:
      return STATUS_PALETTE.baseline;
  }
}

/** Human-readable label for a containment status. */
export function statusLabel(status: ContainmentStatus): string {
  switch (status) {
    case 'OUT_OF_CONTROL':
      return 'Out of control';
    case 'BEING_CONTROLLED':
      return 'Being controlled';
    case 'UNDER_CONTROL':
      return 'Under control';
    case 'PATROL':
      return 'Patrol';
    default:
      return 'Unknown';
  }
}
